import { OrganLayout } from "@/components/OrganLayout";
import { useEffect, useState } from "react";
import { Truck } from "lucide-react";
import { api, fmtCHF, fmtDate } from "@/lib/api";
import { DOC_STATUS } from "@/lib/organs";
import { SectionTitle } from "@/components/Shared";
import DocDetail from "@/components/DocDetail";

export default function PurchasesPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [docs, setDocs] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/modules/digestive").then((r) => setDocs(r.data.documents || [])).catch(() => {});
  }, []);

  const openDoc = async (docId) => {
    const { data } = await api.get(`/documents/${docId}`);
    setDetail(data);
  };

  return (
    <OrganLayout organId="digestive" data-testid="purchases-page" description="Schede fornitore costruite dai documenti reali. Lo storico aiuta DAD, ma il documento corrente ha sempre priorità.">

      <SectionTitle>Fornitori</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-3 mb-6" data-testid="suppliers-grid">
        {suppliers.map((s) => (
          <div key={s.supplier_id} data-testid={`supplier-${s.supplier_id}`} className="glass rounded-3xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-9 h-9 rounded-2xl flex items-center justify-center text-cyan-300"
                style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.15)" }}>
                <Truck size={16} />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{s.name}</div>
                <div className="text-[11px] text-gray-500">{s.doc_count} documenti · {fmtCHF(s.total_amount)}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(s.categories || []).map((c) => <span key={c} className="chip !text-[10px]">{c}</span>)}
              {(s.payment_methods || []).map((p) => <span key={p} className="chip !text-[10px] !text-cyan-200">{p}</span>)}
            </div>
          </div>
        ))}
        {suppliers.length === 0 && (
          <div className="glass rounded-3xl p-8 text-center text-gray-500 text-sm sm:col-span-2" data-testid="suppliers-empty">
            Nessun fornitore ancora conosciuto. DAD crea le schede fornitore automaticamente dai documenti acquisiti.
          </div>
        )}
      </div>

      <SectionTitle>Documenti di acquisto</SectionTitle>
      <div className="space-y-2" data-testid="purchases-docs">
        {docs.map((d) => {
          const st = DOC_STATUS[d.status];
          return (
            <button key={d.doc_id} data-testid={`purchase-doc-${d.doc_id}`} onClick={() => openDoc(d.doc_id)}
              className="w-full text-left glass rounded-2xl px-4 py-3 flex items-center gap-3 hover:border-cyan-400/30 transition-colors">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-gray-200 truncate">{d.supplier || d.filename}</div>
                <div className="text-[11px] text-gray-500">{d.doc_type} · {fmtDate(d.date)}{d.management_category ? ` · ${d.management_category}` : ""}</div>
              </div>
              {st && <span className="text-[10px] shrink-0" style={{ color: st.color }}>{st.label}</span>}
              <span className="font-mono text-sm shrink-0">{fmtCHF(d.amount)}</span>
            </button>
          );
        })}
        {docs.length === 0 && <div className="glass rounded-3xl p-8 text-center text-gray-500 text-sm">Nessun documento di acquisto.</div>}
      </div>

      <DocDetail detail={detail} onClose={() => setDetail(null)} />
    </OrganLayout>
  );
}
